/** @import {FC, MutableRefObject} from 'react' */
import { useCallback, useEffect, useRef } from 'react';
import { Popup } from 'semantic-ui-react';

/** @type {FC<{className?: string, onSubmit: (comment: string) => void, onCancel: () => void, placeholder?: string, resetAfterSubmit?: boolean, canCancelOnEmpty?: boolean, switchFocus?: boolean}>} */
const CommentForm = ({
  className = '',
  onSubmit,
  onCancel,
  placeholder = 'Feedback eingeben…',
  resetAfterSubmit = false,
  canCancelOnEmpty = false,
  switchFocus = false,
}) => {
  /** @type {MutableRefObejct<HTMLFormElement>} */
  const form = useRef(null);
  /** @type {MutableRefObject<HTMLDivElement>} */
  const textarea = useRef(null);
  /** @type {MutableRefObject<HTMLButtonElement>} */
  const submitBtn = useRef(null);
  /** @type {MutableRefObject<HTMLButtonElement>} */
  const resetBtn = useRef(null);

  const innerOnReset = useCallback(() => {
    if (textarea.current != null) {
      textarea.current.innerText = '\n';
      textarea.current.setAttribute('data-placeholder', '');
    }
    if (submitBtn.current != null) submitBtn.current.disabled = true;
    if (resetBtn.current != null) resetBtn.current.disabled = true;
    onCancel();
  }, [onCancel]);

  const innerOnSubmit = useCallback(
    /** @param {SubmitEvent?} event */
    (event) => {
      if (event != null) event.preventDefault();

      if (textarea.current == null) return;

      onSubmit(textarea.current.innerText);

      if (resetAfterSubmit) innerOnReset();
    },
    [innerOnReset, onSubmit, resetAfterSubmit],
  );

  /**
   * @type {(event: KeyboardEvent) => void}
   */
  const keyEventHandler = useCallback(
    (event) => {
      if (
        event.code === 'Escape' &&
        (canCancelOnEmpty ||
          (textarea.current.innerText != null &&
            textarea.current.innerText !== '\n'))
      )
        innerOnReset();
      if (event.code === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        innerOnSubmit();
      }
    },
    [canCancelOnEmpty, innerOnReset, innerOnSubmit],
  );

  /** @type {(event: InputEvent<HTMLDivElement>) => void} */
  const inputHandler = useCallback(
    ({ target }) => {
      if (target.innerText == null || target.innerText === '\n') {
        target.setAttribute('data-placeholder', '');
        if (submitBtn.current != null) submitBtn.current.disabled = true;
        if (resetBtn.current != null && !canCancelOnEmpty)
          resetBtn.current.disabled = true;
      } else {
        target.removeAttribute('data-placeholder');
        if (submitBtn.current != null) submitBtn.current.disabled = false;
        if (resetBtn.current != null) resetBtn.current.disabled = false;
      }
    },
    [canCancelOnEmpty],
  );

  useEffect(() => {
    if (switchFocus && textarea.current != null) textarea.current.focus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <form
      ref={form}
      onSubmit={innerOnSubmit}
      onReset={innerOnReset}
      className={`ui form comment-form ${className}`}
    >
      <div className="ui raised segments">
        <div className="ui segment">
          <Popup
            on="focus"
            trigger={
              <div
                ref={textarea}
                role="textbox"
                aria-multiline="true"
                aria-label={placeholder}
                spellCheck="true"
                contentEditable="true"
                tabIndex={0}
                onKeyDown={keyEventHandler}
                onInput={inputHandler}
                data-placeholder=""
              />
            }
            content={
              <>
                <kbd>Enter</kbd> zum Abschicken.
                <br />
                <kbd>Shift</kbd> + <kbd>Enter</kbd> um eine neue Zeile
                einzufügen.
                <br />
                <kbd>Esc</kbd> zum Abbrechen.
              </>
            }
            size="mini"
            position="bottom right"
          />
        </div>
      </div>
      <div className="ui clearing segment actions">
        <button ref={submitBtn} type="submit" disabled={true}>
          senden
        </button>
        <button ref={resetBtn} type="reset" disabled={!canCancelOnEmpty}>
          abbrechen
        </button>
      </div>
    </form>
  );
};

export default CommentForm;
